import { useMemo } from "react";
import type { EntityItemConfig, EntityPoolConfig } from "@salestrak/ui";
import { getEntityGlyph, type EntityData } from "#app/lib/entity-visuals";
import { poolItemVisual } from "../utils";
import type { Omittable } from "@salestrak/ui";

// Issue types are commonly fetched as part of IssueViewData. Rather than
// coupling this hook to a specific container query, it accepts pre-fetched
// issue types and wraps them in a pool. This keeps the hook usable in any
// context (issue view, settings, filters, etc.).

const POOL_ID = "issue-types";

export type IssueTypeInput = Omittable<{
  issueTypeId: string;
  name: string;
  description?: string | null;
  isDefault?: boolean;
  icon?: { icon?: string | null; color?: string | null } | null;
}>;

export type UseIssueTypePoolOptions = Omittable<{
  /** Pre-fetched issue types */
  issueTypes: IssueTypeInput[];
}>;

/**
 * Wraps pre-fetched issue types into an EntityPoolConfig. A deleted type's
 * issues convert to the workspace default, so there is no "deleted type"
 * placeholder entry to render.
 */
export function useIssueTypePool(options: UseIssueTypePoolOptions) {
  const { issueTypes } = options;

  const items = useMemo<EntityItemConfig[]>(() => {
    const mapped: EntityItemConfig[] = issueTypes.map((t) => {
      const entity: EntityData = {
        kind: "issueType",
        id: t.issueTypeId,
        issueType: t,
      };
      // `render` drives the display (padded rounded square like teams, never a
      // circular initials avatar); the primitive icon/color stay as data so
      // selections round-trip through selectionToIconEntity.
      const glyph = getEntityGlyph(entity);
      return {
        id: t.issueTypeId,
        title: t.name,
        // The row's subtext marks the workspace's default type; a type's
        // description is prose for its settings page, not a picker row.
        description: t.isDefault ? "(Default)" : undefined,
        avatar: {
          icon: glyph.icon ?? undefined,
          color: glyph.color ?? undefined,
          padded: true,
          render: () => poolItemVisual(entity),
        },
        data: { isDefault: t.isDefault },
      };
    });

    return mapped;
  }, [issueTypes]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Project Types",
      items,
    }),
    [items],
  );

  return { pool };
}

export { POOL_ID as ISSUE_TYPE_POOL_ID };
